import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import { BookingContext } from '../context/BookingContext';
import { eventBookingAPI } from '../utils/api';
import TimeSelect from '../components/TimeSelect';
import DateSelect from '../components/DateSelect';
import { dashboardRefreshEmitter } from '../utils/dashboardRefresh';

const PaymentConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addBooking } = useContext(BookingContext);
  const initialDetails = location.state?.eventDetails || {
    eventType: '',
    numberOfGuests: 50,
    eventDate: '',
    eventTimeStart: '',
    eventTimeEnd: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    venue: 'Casa Gatchalian',
    paymentOption: ''
  };

  const [eventDetails, setEventDetails] = useState(initialDetails);

  const [message, setMessage] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Modal state for inline edits (stay on same page)
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'date' | 'guests' | 'eventType'
  const [tempDate, setTempDate] = useState('');
  const [tempGuests, setTempGuests] = useState('');
  const [tempEventType, setTempEventType] = useState('');

  // Get price based on number of guests
  const guestPackages = [
    { pax: 50, price: 59500 },
    { pax: 80, price: 90500 },
    { pax: 100, price: 117000 },
    { pax: 120, price: 135000 },
    { pax: 150, price: 165000 }
  ];

  const selectedPackage = guestPackages.find(pkg => pkg.pax === parseInt(eventDetails.numberOfGuests));
  const totalPrice = selectedPackage?.price || 0;
  const downPayment = totalPrice * 0.25;
  const remainingBalance = totalPrice - downPayment;
  const refundableAmount = totalPrice * 0.25; // 25% of total price is refundable (which is the full deposit)

  const isFullPayment = eventDetails.paymentOption === 'Full Payment';
  const amountToPay = isFullPayment ? totalPrice : downPayment;

  const inclusions = [
    'Buffet Steward',
    'With band/Sound System',
    'Service Attendant',
    'Parking Attendant',
    '4hrs exclusive',
  ];

  // Open modal and initialize temp values
  const openModal = (type) => {
    setModalType(type);
    if (type === 'date') setTempDate(eventDetails.eventDate || '');
    if (type === 'guests') setTempGuests(eventDetails.numberOfGuests || '');
    if (type === 'eventType') setTempEventType(eventDetails.eventType || '');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType(null);
  };

  const saveModal = () => {
    if (modalType === 'date') {
      setEventDetails(prev => ({ ...prev, eventDate: tempDate }));
    }
    if (modalType === 'guests') {
      setEventDetails(prev => ({ ...prev, numberOfGuests: tempGuests }));
    }
    if (modalType === 'eventType') {
      setEventDetails(prev => ({ ...prev, eventType: tempEventType }));
    }
    closeModal();
  };

  const handleConfirmPayment = async () => {
    if (!referenceNumber) {
      setErrorMessage('Please enter your reference number');
      return;
    }

    try {
      // Create event booking object to send to backend
      const eventBookingData = {
        event_type: eventDetails.eventType,
        number_of_guests: parseInt(eventDetails.numberOfGuests),
        event_date: eventDetails.eventDate,
        first_name: eventDetails.firstName,
        last_name: eventDetails.lastName,
        email: eventDetails.email,
        phone: eventDetails.phone,
        payment_option: eventDetails.paymentOption,
        reference_number: referenceNumber,
        amount_to_pay: amountToPay,
        total_price: totalPrice,
        special_requests: ''
      };

      // Send to backend
      const response = await eventBookingAPI.create(eventBookingData);
      
      if (response.data.success) {
        // Add to local context as well
        const eventBooking = {
          id: response.data.data.event_booking_id,
          ...eventBookingData,
          status: 'pending',
          createdAt: new Date().toISOString()
        };
        addBooking(eventBooking);

        // Trigger dashboard refresh for admin
        dashboardRefreshEmitter.emit();
        
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error creating event booking:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to create booking. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-black mb-6 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            <span className="font-medium">Back</span>
          </button>
          <h1 className="text-3xl md:text-3xl font-bold text-gray-900 mb-2">Confirm and pay</h1>
          <p className="text-gray-600">Complete your event booking in just a few steps</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column - Payment Details */}
          <div className="space-y-4 lg:col-span-2">
            {/* Payment Amount Display */}
            <div className="bg-black p-5 rounded-xl shadow-md text-white">
              <div className="text-xs font-medium mb-1 opacity-90">Amount to Pay</div>
              <div className="text-3xl font-bold mb-3">₱{amountToPay.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</div>
              {!isFullPayment && (
                <div className="text-xs bg-white/20 backdrop-blur-sm p-2.5 rounded-lg">
                  <div className="font-semibold">Down Payment (25%)</div>
                  <div className="text-xs mt-0.5">Remaining ₱{remainingBalance.toLocaleString('en-PH', { minimumFractionDigits: 2 })} due on event date</div>
                </div>
              )}
            </div>

            {/* GCash Payment Section */}
            <div className="bg-white p-5 rounded-xl shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-xs font-bold mr-2">1</div>
                <h2 className="text-base font-bold text-gray-900">Scan the QR Code</h2>
              </div>
              
              {/* QR Code */}
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-6 rounded-lg border-2 border-gray-300 mb-4">
                <div className="flex flex-col items-center justify-center">
                  <div className="w-64 h-64 bg-white rounded-lg shadow-inner flex items-center justify-center p-2">
                    <img 
                      src="/images/instapay.jpg" 
                      alt="Instapay QR Code"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Upload Proof of Payment */}
            <div className="bg-white p-5 rounded-xl shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-xs font-bold mr-2">2</div>
                <h2 className="text-base font-bold text-gray-900">Upload Proof of Payment</h2>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Reference Number *</label>
                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => {
                    setReferenceNumber(e.target.value);
                    setErrorMessage('');
                  }}
                  placeholder="Enter 13-digit reference number"
                  className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                />
                {errorMessage && (
                  <p className="text-red-600 text-xs font-medium mt-1">{errorMessage}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleConfirmPayment}
              className="w-full bg-black text-white py-3.5 rounded-lg text-base font-bold hover:bg-gray-800 transition-all shadow-md hover:shadow-lg"
            >
              Confirm Booking
            </button>

            <p className="text-center text-xs text-gray-500">
              We will verify your payment and contact you within 24 hours
            </p>
          </div>

          {/* Right Column - Booking Summary */}
          <div className="lg:sticky lg:top-8 h-fit">
            <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 max-w-sm">
              {/* Header with Venue Name */}

              {/* Cancellation Policy */}
              <div className="mb-3 pb-3 border-b border-gray-100">
                <h4 className="text-2xl font-semibold text-gray-900 mb-0.5">Event Booking Details</h4>
                <p className="text-sm text-gray-600 leading-tight">
                  Half of the DP is refundable within 24 hours.
                </p>
                <p className="text-sm text-gray-600 leading-tight">
                  For cancellation please contact the management.
                </p>
              </div>

              {/* Dates Section */}
              <div className="mb-3 pb-3 border-b border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-0.5">Dates</h4>
                    <p className="text-sm text-gray-900">{eventDetails.eventDate}</p>
                  </div>
                  <button
                    onClick={() => openModal('date')}
                    className="text-sm font-medium underline text-gray-900 ml-2"
                  >
                    Change
                  </button>
                </div>
              </div>

              {/* Guests Section */}
              <div className="mb-3 pb-3 border-b border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-0.5">Guests</h4>
                    <p className="text-sm text-gray-900">{eventDetails.numberOfGuests} pax</p>
                  </div>
                  <button
                    onClick={() => openModal('guests')}
                    className="text-sm font-medium underline text-gray-900 ml-2"
                  >
                    Change
                  </button>
                </div>
              </div>

              {/* Event Type Section */}
              <div className="mb-3 pb-3 border-b border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-0.5">Event Type</h4>
                    <p className="text-sm text-gray-900">{eventDetails.eventType}</p>
                  </div>
                  <button
                    onClick={() => openModal('eventType')}
                    className="text-sm font-medium underline text-gray-900 ml-2"
                  >
                    Change
                  </button>
                </div>
              </div>

              {/* Price details Section */}
              <div className="mb-3 pb-3 border-b border-gray-100">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">Price details</h4>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">{eventDetails.numberOfGuests} pax</span>
                  <span className="text-sm font-semibold text-gray-900">₱{totalPrice.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              {/* Total Section */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-base font-semibold text-gray-900">Total PHP</span>
                  <span className="text-base font-semibold text-gray-900">₱{amountToPay.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                </div>
                {!isFullPayment && (
                  <p className="text-sm text-gray-600 leading-tight">
                    Down Payment (25%). Remaining ₱{remainingBalance.toLocaleString('en-PH', { minimumFractionDigits: 2 })} due on event date
                  </p>
                )}
              </div>
              {/* Inline Edit Modal */}
              {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
                  <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {modalType === 'date' && 'Change dates'}
                        {modalType === 'guests' && 'Change guests'}
                        {modalType === 'eventType' && 'Change event type'}
                      </h3>
                    </div>
                    <div className="p-4">
                      {modalType === 'date' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Event Date</label>
                          <div className="flex justify-center">
                            <DateSelect
                              value={tempDate}
                              onChange={setTempDate}
                              min={new Date().toISOString().slice(0, 10)}
                            />
                          </div>
                        </div>
                      )}

                      {modalType === 'guests' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Number of Guests</label>
                          <select
                            value={tempGuests}
                            onChange={(e) => setTempGuests(e.target.value)}
                            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                          >
                            {guestPackages.map(p => (
                              <option key={p.pax} value={p.pax}>{p.pax} pax</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {modalType === 'eventType' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Event Type</label>
                          <select
                            value={tempEventType}
                            onChange={(e) => setTempEventType(e.target.value)}
                            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                          >
                            <option value="">Select event type</option>
                            <option>Weddings</option>
                            <option>Anniversaries</option>
                            <option>Birthdays</option>
                            <option>Christenings</option>
                            <option>Corporate Events</option>
                            <option>Private Meetings</option>
                            <option>Other Occasion</option>
                          </select>
                        </div>
                      )}
                    </div>
                    <div className="p-3 border-t border-gray-200 flex justify-end gap-2">
                      <button onClick={closeModal} className="px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">Cancel</button>
                      <button onClick={saveModal} className="px-3 py-1.5 rounded-md bg-black text-white text-sm font-semibold hover:bg-gray-800 transition-colors">Save</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckIcon className="w-8 h-8 text-green-600" />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Submitted Successfully!</h2>
                <p className="text-gray-600 text-sm mb-6">
                  We will verify your payment and contact you shortly to confirm your booking.
                </p>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate('/my-bookings');
                  }}
                  className="w-full bg-black text-white py-2.5 rounded-lg font-semibold hover:bg-gray-800 transition-all"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentConfirmation;
